import React, { useEffect, useRef, createRef } from 'react'
import animateScrollTo from 'animated-scroll-to'
import { updateSearchParams } from '../../search/updateSearchParams.js'
import {
  resultsPerPage,
  setFirstAndLastIndex,
  clinicIndexToCardIndex,
} from './resultsUtils.js'
import ClinicCard from '../ClinicCard/ClinicCard.jsx'
import PageNumberBar from '../../search/PageNumberBar/PageNumberBar.jsx'
import ShowingResultsCount from '../../search/PageNumberBar/ShowingResultsCount.jsx'
import './ResultsList.scss'

const MAX_CLINICS_PER_PAGE = 5

const ResultsList = ({
  lexicon,
  sortedAndFilteredClinics,
  cardDisplayState,
  updateCardDisplayState,
}) => {
  const clinicCount = sortedAndFilteredClinics.length
  const pageNumbersToShow =
    clinicCount > 20
      ? [1, 2, 3, 4, 5]
      : [...Array(Math.ceil(clinicCount / 5)).keys()].map((i) => i + 1)

  const [firstClinicIndex, lastClinicIndex] = setFirstAndLastIndex(
    cardDisplayState.isMobileMapOpen,
    cardDisplayState.activePageNum
  )

  const paginatedClinics = sortedAndFilteredClinics.slice(
    firstClinicIndex,
    lastClinicIndex
  )
  const cardRefs = useRef(
    Array.from({ length: resultsPerPage }, () => createRef())
  )

  const getScrollTargetForCard = (cardIndex) => {
    if (
      !cardRefs.current ||
      !cardRefs.current[cardIndex] ||
      !cardRefs.current[cardIndex].current
    )
      return undefined

    const elementPos = cardRefs.current[cardIndex].current.offsetTop
    const spaceToShowFilterRibbon = cardIndex === 0 ? 110 : 0
    const spaceForMobileHeader = 100
    let scrollTarget =
      elementPos - spaceForMobileHeader - spaceToShowFilterRibbon

    // don't scroll down past the edge of the map
    const desktopTotalFooterHeight = 677 // could probably try to calculate this
    const totalPageHeight =
      document.height !== undefined
        ? document.height
        : document.body.offsetHeight
    const lowestScrollTarget =
      totalPageHeight - desktopTotalFooterHeight - window.innerHeight

    scrollTarget = Math.min(scrollTarget, lowestScrollTarget)
    return scrollTarget
  }

  const scrollToSelectedClinic = (index) => {
    const scrollTarget = getScrollTargetForCard(index) || 0
    setTimeout(() => {
      animateScrollTo(scrollTarget, { speed: 800 })
    }, 50)
  }

  useEffect(() => {
    const cardIndex = clinicIndexToCardIndex(
      cardDisplayState.selectedClinicIndex
    )

    if (cardRefs.current[cardIndex]) scrollToSelectedClinic(cardIndex)
  }, [cardDisplayState.selectedClinicIndex])

  // when we switch to a new page, also update the URL and history
  const updatePageNumber = (pageNum) => {
    const scrollTarget = getScrollTargetForCard(0) || 0
    animateScrollTo(scrollTarget, { speed: 800 })
    updateSearchParams({ page: pageNum })
  }

  // When the page changes...
  useEffect(() => {
    updateCardDisplayState({
      selectedClinicIndex: null,
      focusedPinIndex: null,
    })

    // if (paginatedClinics.length) {
    //   cardRefs.current = [...Array(paginatedClinics.length).keys()].map((_, i) => cardRefs.current[i] || createRef());
    // }
  }, [cardDisplayState.activePageNum])
  //
  // useEffect(() => {
  //   prevPageNum.current = activePageNum;
  // });

  return (
    <div className="ResultsList">
      <div className="scroll-view">
        {clinicCount > 0 && (
          <ShowingResultsCount
            lexicon={lexicon}
            total={clinicCount}
            num1={cardDisplayState.activePageNum * MAX_CLINICS_PER_PAGE - 4}
            num2={Math.min(
              cardDisplayState.activePageNum * MAX_CLINICS_PER_PAGE,
              clinicCount
            )}
          />
        )}

        {paginatedClinics.map((clinic, i) => {
          const clinicIndex = firstClinicIndex + i

          return (
            <ClinicCard
              lexicon={lexicon.subset('ClinicCard')}
              cardRef={cardRefs.current[i]}
              clinic={clinic}
              clinicNum={clinicIndex + 1}
              onMouseEnter={() =>
                updateCardDisplayState({ hoveringClinicIndex: clinicIndex })
              }
              onMouseLeave={() =>
                updateCardDisplayState({ hoveringClinicIndex: -1 })
              }
              isSelected={
                parseInt(cardDisplayState.selectedClinicIndex) -
                  firstClinicIndex ===
                  i || cardDisplayState.focusedPinIndex === clinicIndex
              }
              isCondensedMobileMapView={false}
              isDesktopHovering={
                cardDisplayState.hoveringClinicIndex === clinicIndex
              }
              key={clinic.id}
            />
          )
        })}
      </div>

      <PageNumberBar
        setActivePageNum={(activePageNum) =>
          updateCardDisplayState({ activePageNum })
        }
        activePageNum={cardDisplayState.activePageNum}
        pageNumbersToShow={pageNumbersToShow}
        totalClinicsSize={sortedAndFilteredClinics.length}
        additionalClickHandler={updatePageNumber}
      />

      {/*
      <div className="sources">
        <Markdown>
          {lexicon.get('sources')}
        </Markdown>
      </div>
      */}
    </div>
  )
}

export default ResultsList
